import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { Navigate, Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiCancel } from '@mdi/js';

/**
 * AdminGuard Wrapper
 * Use this to wrap routes that should only be accessible by Site Admins.
 * It checks the 'system/admins' collection for a document matching the user's UID.
 */
const AdminGuard = ({ children }) => {
    const [authState, setAuthState] = useState({
        isLoading: true,
        isAdmin: false,
        user: null,
    });

    const auth = getAuth();
    const db = getFirestore();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                setAuthState({ isLoading: false, isAdmin: false, user: null });
                return;
            }

            try {
                // Path: /system/admins/{uid}
                // Ensure this document exists in your Emulator for your specific UID
                console.log('user.uid', user.uid);
                const adminDocRef = doc(db, 'system', 'admins', 'uids', user.uid);
                const adminSnap = await getDoc(adminDocRef);

                setAuthState({
                    isLoading: false,
                    isAdmin: adminSnap.exists(),
                    user: user,
                });
            } catch (error) {
                console.error("Error checking admin status:", error);
                // Fallback to non-admin on error for security
                setAuthState({ isLoading: false, isAdmin: false, user: user });
            }
        });

        return () => unsubscribe();
    }, [auth, db]);

    // Loading State
    if (authState.isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-900 text-white">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-sm font-mono tracking-widest uppercase opacity-70">Verifying Admin Privileges...</p>
                </div>
            </div>
        );
    }

    // Not Logged In
    if (!authState.user) {
        return <Navigate to="/" replace />;
    }

    // Logged in but NOT an admin
    if (!authState.isAdmin) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-900 text-white p-6">
                <div>
                    <div className="inline-flex items-center justify-center">
                        <Icon path={mdiCancel} color="red" size="6em" />
                    </div>
                    <h1 className="text-2xl font-black mb-2 tracking-tight">Access Denied</h1>
                    <p className="text-slate-400 text-sm mb-8">
                        Your account <span className="text-slate-200 font-mono text-xs">({authState.user.email})</span> does not have site administrator privileges.
                    </p>
                    <div>
                        <button
                            onClick={() => auth.signOut()}
                            className="block w-full py-2 text-xs text-slate-500 hover:text-red-400 transition-colors underline">
                            Sign out and try another account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Success: Render Admin Content
    return children;
};

export default AdminGuard;